import { HoverableLinkSizes } from '@paywithextend/components'
import cn from 'classnames'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import CodeInput from 'ext-ui/components/CodeInput/CodeInput'
import React, { ReactElement, useState } from 'react'
import { useSelector } from 'react-redux'
import CardsIllustration from 'src/assets/images/cards-illustration.png'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { SupportedBrands } from 'src/enums'
import Trns, { useTranslate } from 'src/localization/Trns'
import { selectMfaSentFromState } from 'src/redux/selectors/auth.selectors'
import { selectBrandName } from 'src/redux/selectors/session.selectors'

import style from './VerifyEmailModal.style'

type VerifyEmailModalProps = {
  className?: string
  isOpen: boolean
  email: string
  isVerifying: boolean
  verifyError?: string
  resendError?: string
  verificationResent: boolean
  onVerify: (code: string) => void
  onResend: (email: string) => void
  onGoBack: () => void
  hideGoBack?: boolean
}

export default function VerifyEmailModal(
  props: VerifyEmailModalProps,
): ReactElement {
  const {
    className = '',
    isOpen,
    email,
    isVerifying,
    verifyError,
    resendError,
    verificationResent,
    onVerify,
    onResend,
    onGoBack,
    hideGoBack = false,
  } = props

  const translate = useTranslate()
  const hideImages = useSelector(selectBrandName) === SupportedBrands.Jpm
  const mfaCodeSent = useSelector(selectMfaSentFromState)

  const [code, setCode] = useState('')
  const [showCountdownError, setShowCountdownError] = useState(false)
  const [sendNewCodeClicked, setSendNewCodeClicked] = useState(false)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    onVerify(code)
  }

  const handleSendNewCode = (): void => {
    setSendNewCodeClicked(true)
    if (!mfaCodeSent) {
      onResend(email)
      setShowCountdownError(false)
    } else {
      setShowCountdownError(true)
    }
  }

  return (
    <BaseModal
      className={cn('VerifyEmailModal', className)}
      isOpen={isOpen}
      onClose={onGoBack}
      showCloseButton={false}
      css={style}
      aria-label={translate('Verify Email')}
    >
      {!hideImages && (
        <img
          className="verify-email-decorative-image"
          src={CardsIllustration}
          alt=""
          role="presentation"
        />
      )}
      <form
        className="verify-email-form"
        onSubmit={handleSubmit}
        data-testid="verifyEmailModalForm"
      >
        <h2 className="verify-email-title">
          <Trns source="Verify Email" />
        </h2>

        <p className="verify-email-subtitle">
          <Trns
            source="Enter the verification code sent to ${destination}"
            expressions={{
              destination: <span className="bold-email">{email}</span>,
            }}
          />
        </p>

        {!hideGoBack && (
          <div className="verify-email-wrong-email">
            <Trns
              source="Wrong email? ${goBack}"
              expressions={{
                goBack: (
                  <HoverableLink
                    underline
                    size={HoverableLinkSizes.Medium}
                    onClick={onGoBack}
                    ariaLabelSource="Go back"
                  >
                    <Trns source="Go back" />
                  </HoverableLink>
                ),
              }}
            />
          </div>
        )}

        <CodeInput
          label={translate('Verification Code')}
          value={code}
          onChange={setCode}
          errorText={verifyError}
          disabled={isVerifying}
          testId="verifyEmailCode"
        />

        <Button
          id="verifyEmailModalBtn"
          type={ButtonTypes.submit}
          design={ButtonDesigns.designCta}
          text={<Trns source="Verify Email" />}
          loading={isVerifying}
          disabled={isVerifying || code.length < 6}
          data-testid="verifyEmailModalBtn"
          big
        />

        <div className="verify-email-actions">
          <HoverableLink
            underline
            size={HoverableLinkSizes.Medium}
            onClick={handleSendNewCode}
            ariaLabelSource="Send New Code"
            testId="verifyEmailSendNewCode"
          >
            <Trns source="Send New Code" />
          </HoverableLink>

          <div className="verify-email-feedback">
            {showCountdownError && (
              <span role="alert">
                <Trns source="Please wait a few seconds before requesting a new verification code" />
              </span>
            )}
            {verificationResent &&
              sendNewCodeClicked &&
              !showCountdownError && (
                <span role="status">
                  <Trns source="New code sent" />
                </span>
              )}
            {resendError && <span role="alert">{resendError}</span>}
          </div>
        </div>
      </form>
    </BaseModal>
  )
}
